import { FormEvent, useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { fetchProof, ProofData } from '../../services/proofService';
import {
  uploadAdminProof,
  updateOrderWorkStatus,
  updateOrderShipping,
  updateOrderPrice,
  sendAdminProofMessage,
  fetchAdminOrderDetail,
  convertInquiryToOrder,
  updateOrderPaymentTerms,
  AdminOrderDetail as AdminOrderDetailType,
} from './adminService';
import { useAdminSession } from './adminSession';
import { ORDER_STATUS_LABEL, WORK_STATUS_LABEL, WorkStatus } from '../../types/order';
import { CARRIERS } from '../../types/carriers';
import { PAYMENT_TERMS_LABEL, PaymentTerms, SHIPPING_METHOD_LABEL } from '../../types/orderPolicy';
import { DESIGN_TYPE_LABEL } from '../Order/orderMenu';
import { REVISION_ITEM_LABEL, RevisionItem } from '../MyPage/proof';
import { formatPhone } from '../../utils/formatPhone';
import styles from './Admin.module.scss';
import { apiUrl } from '../../services/apiClient';

function formatDateTime(value: string | null): string {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString('ko-KR');
}

function revisionItemLabels(value: string | null | undefined): string[] {
  if (!value) return [];
  return value
    .split(',')
    .map((item) => REVISION_ITEM_LABEL[item as RevisionItem])
    .filter(Boolean);
}

function AdminOrderDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [session] = useAdminSession();
  const [order, setOrder] = useState<AdminOrderDetailType | null>(null);
  const [proof, setProof] = useState<ProofData | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [workStatusError, setWorkStatusError] = useState<string | null>(null);
  const [isUpdatingWorkStatus, setIsUpdatingWorkStatus] = useState(false);
  const [carrier, setCarrier] = useState('');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [shippingError, setShippingError] = useState<string | null>(null);
  const [isSubmittingShipping, setIsSubmittingShipping] = useState(false);
  const [quotePrice, setQuotePrice] = useState('');
  const [priceError, setPriceError] = useState<string | null>(null);
  const [isSavingPrice, setIsSavingPrice] = useState(false);
  const [replyMessage, setReplyMessage] = useState('');
  const [replyError, setReplyError] = useState<string | null>(null);
  const [isSendingReply, setIsSendingReply] = useState(false);
  const [convertError, setConvertError] = useState<string | null>(null);
  const [paymentTerms, setPaymentTerms] = useState<PaymentTerms>('PREPAID');
  const [paymentDueDate, setPaymentDueDate] = useState('');
  const [termsError, setTermsError] = useState<string | null>(null);
  const [isSavingTerms, setIsSavingTerms] = useState(false);

  const loadProof = () => {
    if (!id || !session) return;
    fetchProof(id, session.token)
      .then((data) => {
        setProof(data);
        setCarrier(data.carrier ?? '');
        setTrackingNumber(data.trackingNumber ?? '');
      })
      .catch((err) => setLoadError(err instanceof Error ? err.message : '시안 정보를 불러오지 못했습니다.'));
  };

  useEffect(loadProof, [id, session]);

  useEffect(() => {
    if (!id || !session) return;
    fetchAdminOrderDetail(id, session.token)
      .then((data) => {
        setOrder(data);
        if (data.total_price != null) setQuotePrice(String(data.total_price - (data.revision_fee || 0)));
        setPaymentTerms(data.payment_terms ?? 'PREPAID');
        setPaymentDueDate(data.payment_due_date ? String(data.payment_due_date).slice(0, 10) : '');
      })
      .catch((err) => setLoadError(err instanceof Error ? err.message : '주문 정보를 불러오지 못했습니다.'));
  }, [id, session]);

  const handleWorkStatusChange = async (workStatus: WorkStatus) => {
    if (!id || !session) return;
    setWorkStatusError(null);
    setIsUpdatingWorkStatus(true);
    try {
      const result = await updateOrderWorkStatus(id, workStatus, session.token);
      setProof((prev) =>
        prev
          ? {
              ...prev,
              workStatus: result.work_status,
              workStartedAt: result.work_started_at,
              workCompletedAt: result.work_completed_at,
            }
          : prev,
      );
    } catch (err) {
      setWorkStatusError(err instanceof Error ? err.message : '작업 상태 변경 중 오류가 발생했습니다.');
    } finally {
      setIsUpdatingWorkStatus(false);
    }
  };

  const handlePriceSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!id || !session) return;

    setPriceError(null);
    setIsSavingPrice(true);
    try {
      const result = await updateOrderPrice(id, Number(quotePrice), session.token);
      setOrder((prev) => (prev ? { ...prev, total_price: result.totalPrice, revision_fee: result.revisionFee } : prev));
    } catch (err) {
      setPriceError(err instanceof Error ? err.message : '금액 저장 중 오류가 발생했습니다.');
    } finally {
      setIsSavingPrice(false);
    }
  };

  const handleConvert = async () => {
    if (!id || !session) return;
    setConvertError(null);
    try {
      await convertInquiryToOrder(id, session.token);
      setOrder((prev) => (prev ? { ...prev, request_type: 'ORDER' } : prev));
    } catch (err) {
      setConvertError(err instanceof Error ? err.message : '주문 전환 중 오류가 발생했습니다.');
    }
  };

  const handleTermsSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!id || !session) return;

    setTermsError(null);
    setIsSavingTerms(true);
    try {
      const result = await updateOrderPaymentTerms(
        id,
        paymentTerms,
        paymentTerms === 'POSTPAID' ? paymentDueDate : null,
        session.token,
      );
      setOrder((prev) =>
        prev ? { ...prev, payment_terms: result.paymentTerms, payment_due_date: result.paymentDueDate } : prev,
      );
    } catch (err) {
      setTermsError(err instanceof Error ? err.message : '결제 방식 변경 중 오류가 발생했습니다.');
    } finally {
      setIsSavingTerms(false);
    }
  };

  const handleShippingSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!id || !session || !carrier || !trackingNumber) return;

    setShippingError(null);
    setIsSubmittingShipping(true);
    try {
      const result = await updateOrderShipping(id, carrier, trackingNumber, session.token);
      setProof((prev) =>
        prev
          ? { ...prev, status: result.status, carrier: result.carrier, trackingNumber: result.trackingNumber }
          : prev,
      );
    } catch (err) {
      setShippingError(err instanceof Error ? err.message : '배송 정보 등록 중 오류가 발생했습니다.');
    } finally {
      setIsSubmittingShipping(false);
    }
  };

  const handleReplySubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!id || !session || !replyMessage.trim()) return;

    setReplyError(null);
    setIsSendingReply(true);
    try {
      await sendAdminProofMessage(id, replyMessage.trim(), session.token);
      setReplyMessage('');
      loadProof();
    } catch (err) {
      setReplyError(err instanceof Error ? err.message : '답변 등록 중 오류가 발생했습니다.');
    } finally {
      setIsSendingReply(false);
    }
  };

  const handleUpload = async (event: FormEvent) => {
    event.preventDefault();
    if (!id || !file || !session) return;

    setUploadError(null);
    setIsUploading(true);
    try {
      await uploadAdminProof(id, file, session.token);
      setFile(null);
      loadProof();
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : '시안 업로드 중 오류가 발생했습니다.');
    } finally {
      setIsUploading(false);
    }
  };

  const isInquiry = order?.request_type === 'INQUIRY';
  const listPath = isInquiry ? '/admin/inquiries' : '/admin/orders';

  // 상담 히스토리에서 고객 수정요청에 회차 번호를 붙인다 (오래된 순)
  const timeline = proof ? proof.feedbacks.slice().reverse() : [];
  let revisionRound = 0;
  const rounds = timeline.map((f) => (f.sender === 'CUSTOMER' && f.kind === 'REVISION' ? ++revisionRound : 0));

  return (
    <AdminLayout title={isInquiry ? '견적문의 상세' : '주문 상세'}>
      <div className="section">
        <div className="sec-head">
          <div className="sec-title">{order ? order.order_no : `주문 ID: ${id}`}</div>
          <div className="sec-actions">
            {isInquiry && (
              <button type="button" className="btn btn-primary" onClick={handleConvert}>
                주문으로 전환
              </button>
            )}
            {order?.total_price != null && (
              <Link to={`/admin/orders/${id}/estimate`} className="btn btn-outline">
                견적서 출력
              </Link>
            )}
            <button type="button" className="btn btn-outline" onClick={() => navigate(listPath)}>
              ← {isInquiry ? '견적문의 목록으로' : '주문 목록으로'}
            </button>
          </div>
        </div>

        {convertError && (
          <div className="card" style={{ color: '#B91C1C', marginBottom: 16 }}>
            {convertError}
          </div>
        )}

        {loadError && (
          <div className="card" style={{ color: '#B91C1C', marginBottom: 16 }}>
            {loadError}
          </div>
        )}

        {order && (
          <div className="card" style={{ padding: 0, marginBottom: 16 }}>
            <table>
              <tbody>
                {order.title && (
                  <tr>
                    <th style={{ width: 160 }}>제목</th>
                    <td>{order.title}</td>
                  </tr>
                )}
                {order.company && (
                  <tr>
                    <th>업체명</th>
                    <td>{order.company}</td>
                  </tr>
                )}
                <tr>
                  <th style={{ width: 160 }}>접수 구분</th>
                  <td>
                    {isInquiry ? '견적문의' : '주문'}
                    {order.design_type && ` · ${DESIGN_TYPE_LABEL[order.design_type]}`}
                  </td>
                </tr>
                <tr>
                  <th>담당자</th>
                  <td>{order.customer_name}</td>
                </tr>
                {order.email && (
                  <tr>
                    <th>이메일</th>
                    <td>{order.email}</td>
                  </tr>
                )}
                <tr>
                  <th>연락처</th>
                  <td>{formatPhone(order.phone)}</td>
                </tr>
                {order.shape && (
                  <tr>
                    <th>패키지 형태</th>
                    <td>{order.shape}</td>
                  </tr>
                )}
                <tr>
                  <th>제작수량</th>
                  <td>{order.quantity.toLocaleString()}개</td>
                </tr>
                <tr>
                  <th>배송 방법</th>
                  <td>{SHIPPING_METHOD_LABEL[order.shipping_method] ?? '단건발송'}</td>
                </tr>
                {order.box_size && (
                  <tr>
                    <th>박스 사이즈</th>
                    <td>{order.box_size}</td>
                  </tr>
                )}
                {order.content_size && (
                  <tr>
                    <th>내용물 사이즈</th>
                    <td>{order.content_size}</td>
                  </tr>
                )}
                {order.content_type && (
                  <tr>
                    <th>내용물 종류</th>
                    <td>{order.content_type}</td>
                  </tr>
                )}
                {order.deadline && (
                  <tr>
                    <th>납기희망일</th>
                    <td>{order.deadline}</td>
                  </tr>
                )}
                {order.budget && (
                  <tr>
                    <th>예산 범위</th>
                    <td>{order.budget}</td>
                  </tr>
                )}
                {order.print_option && (
                  <tr>
                    <th>인쇄 옵션</th>
                    <td>{order.print_option}</td>
                  </tr>
                )}
                {order.route && (
                  <tr>
                    <th>유입경로</th>
                    <td>{order.route}</td>
                  </tr>
                )}
                {order.reference_portfolio && (
                  <tr>
                    <th>참고 포트폴리오</th>
                    <td>{order.reference_portfolio}</td>
                  </tr>
                )}
                {order.content && (
                  <tr>
                    <th>문의내용</th>
                    <td style={{ whiteSpace: 'pre-wrap' }}>{order.content}</td>
                  </tr>
                )}
                {order.attachments.length > 0 && (
                  <tr>
                    <th>첨부파일</th>
                    <td>
                      {order.attachments.map((file) => (
                        <div key={file.id}>
                          <a href={apiUrl(`/uploads/${file.file_path}`)} target="_blank" rel="noopener noreferrer">
                            {file.file_name}
                          </a>
                        </div>
                      ))}
                    </td>
                  </tr>
                )}
                <tr>
                  <th>접수일</th>
                  <td>{new Date(order.created_at).toLocaleString()}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {order && (
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-title" style={{ marginBottom: 12 }}>
              결제 금액
            </div>
            <p style={{ marginBottom: 12, fontSize: 13, color: 'var(--ink-500)' }}>
              {order.total_price != null
                ? `현재 결제금액: ₩ ${order.total_price.toLocaleString()}${
                    order.revision_fee > 0 ? ` (유료 수정비 ₩ ${order.revision_fee.toLocaleString()} 포함)` : ''
                  }`
                : '아직 금액이 확정되지 않았습니다. 금액을 저장해야 고객이 최종 승인 후 결제할 수 있습니다.'}
            </p>
            <form className={styles.uploadForm} onSubmit={handlePriceSubmit}>
              <label htmlFor="quotePrice">견적 금액 (원, 유료 수정비 제외)</label>
              <input
                id="quotePrice"
                type="number"
                min={1}
                step={1}
                value={quotePrice}
                onChange={(event) => setQuotePrice(event.target.value)}
                placeholder="예: 350000"
              />
              <button type="submit" disabled={!quotePrice || Number(quotePrice) < 1 || isSavingPrice}>
                {isSavingPrice ? '저장 중...' : '금액 저장'}
              </button>
              {priceError && <p className={styles.error}>{priceError}</p>}
            </form>

            <div className="card-title" style={{ margin: '20px 0 8px' }}>
              결제 방식
            </div>
            <p style={{ marginBottom: 12, fontSize: 13, color: 'var(--ink-500)' }}>
              {order.paid_at
                ? `결제 완료 (${formatDateTime(order.paid_at)}${
                    order.paid_amount != null ? ` · ₩ ${order.paid_amount.toLocaleString()}` : ''
                  })`
                : order.payment_terms === 'POSTPAID'
                  ? `후불결제 승인됨 — 결제 전에도 제작·배송을 진행할 수 있습니다.${
                      order.payment_due_date ? ` 결제 기한: ${String(order.payment_due_date).slice(0, 10)}` : ''
                    }`
                  : '선결제 — 고객이 결제를 완료해야 배송 정보를 등록할 수 있습니다. 결제 전 발송이 필요하면 후불결제로 전환하세요.'}
            </p>
            {!order.paid_at && (
              <form className={styles.uploadForm} onSubmit={handleTermsSubmit}>
                <label htmlFor="paymentTerms">결제 방식</label>
                <select
                  id="paymentTerms"
                  value={paymentTerms}
                  onChange={(event) => setPaymentTerms(event.target.value as PaymentTerms)}
                >
                  {(Object.keys(PAYMENT_TERMS_LABEL) as PaymentTerms[]).map((terms) => (
                    <option key={terms} value={terms}>
                      {PAYMENT_TERMS_LABEL[terms]}
                    </option>
                  ))}
                </select>
                {paymentTerms === 'POSTPAID' && (
                  <>
                    <label htmlFor="paymentDueDate">결제 기한</label>
                    <input
                      id="paymentDueDate"
                      type="date"
                      value={paymentDueDate}
                      onChange={(event) => setPaymentDueDate(event.target.value)}
                    />
                  </>
                )}
                <button type="submit" disabled={isSavingTerms || (paymentTerms === 'POSTPAID' && !paymentDueDate)}>
                  {isSavingTerms ? '저장 중...' : '결제 방식 저장'}
                </button>
                {termsError && <p className={styles.error}>{termsError}</p>}
              </form>
            )}
          </div>
        )}

        {proof && (
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-title" style={{ marginBottom: 12 }}>
              작업 진행 상태
            </div>
            <p>
              현재 작업 상태:{' '}
              <span
                className={`badgePill ${
                  proof.workStatus === 'COMPLETED' ? 'ok' : proof.workStatus === 'IN_PROGRESS' ? 'info' : 'warn'
                }`}
              >
                <span className="pulse"></span> {WORK_STATUS_LABEL[proof.workStatus]}
              </span>
            </p>
            <p style={{ marginTop: 8, fontSize: 13, color: 'var(--ink-500)' }}>
              작업 시작: {formatDateTime(proof.workStartedAt)} · 작업 완료: {formatDateTime(proof.workCompletedAt)}
            </p>
            <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
              <button
                type="button"
                className="btn btn-outline"
                disabled={isUpdatingWorkStatus || proof.workStatus === 'IN_PROGRESS'}
                onClick={() => handleWorkStatusChange('IN_PROGRESS')}
              >
                작업 시작(작업중)으로 변경
              </button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={isUpdatingWorkStatus || proof.workStatus === 'COMPLETED'}
                onClick={() => handleWorkStatusChange('COMPLETED')}
              >
                작업완료로 변경
              </button>
              {proof.workStatus !== 'WAITING' && (
                <button
                  type="button"
                  className="btn btn-outline"
                  disabled={isUpdatingWorkStatus}
                  onClick={() => handleWorkStatusChange('WAITING')}
                >
                  주문확인 상태로 되돌리기
                </button>
              )}
            </div>
            {workStatusError && (
              <p className={styles.error} style={{ marginTop: 10 }}>
                {workStatusError}
              </p>
            )}
          </div>
        )}

        {proof && (
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-title" style={{ marginBottom: 12 }}>
              배송 정보
            </div>
            {proof.trackingNumber ? (
              <p style={{ marginBottom: 12, fontSize: 13, color: 'var(--ink-500)' }}>
                등록된 송장: {proof.carrier} / {proof.trackingNumber}
              </p>
            ) : (
              <p style={{ marginBottom: 12, fontSize: 13, color: 'var(--ink-500)' }}>
                아직 송장이 등록되지 않았습니다. 등록 시 주문 상태가 배송중으로 변경됩니다.
              </p>
            )}
            <form className={styles.uploadForm} onSubmit={handleShippingSubmit}>
              <label htmlFor="carrier">택배사</label>
              <select id="carrier" value={carrier} onChange={(event) => setCarrier(event.target.value)}>
                <option value="">택배사 선택</option>
                {CARRIERS.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.label}
                  </option>
                ))}
              </select>
              <label htmlFor="trackingNumber">송장번호</label>
              <input
                id="trackingNumber"
                type="text"
                value={trackingNumber}
                onChange={(event) => setTrackingNumber(event.target.value)}
                placeholder="송장번호를 입력해주세요"
              />
              <button type="submit" disabled={!carrier || !trackingNumber || isSubmittingShipping}>
                {isSubmittingShipping ? '등록 중...' : '배송 정보 등록'}
              </button>
              {shippingError && <p className={styles.error}>{shippingError}</p>}
            </form>
          </div>
        )}

        {proof && (
          <div className="card" style={{ marginBottom: 16 }}>
            <p>
              현재 상태:{' '}
              <span className="badgePill info">
                <span className="pulse"></span> {ORDER_STATUS_LABEL[proof.status]}
              </span>
            </p>
            {proof.proofFile ? (
              <p style={{ marginTop: 10 }}>
                현재 시안 (v{proof.proofFile.version}):{' '}
                <a href={apiUrl(`/uploads/${proof.proofFile.file_url}`)} target="_blank" rel="noopener noreferrer">
                  파일 보기
                </a>
              </p>
            ) : (
              <p style={{ marginTop: 10, color: 'var(--ink-500)' }}>등록된 시안이 없습니다.</p>
            )}
            {proof.revision && (
              <div className={styles.revisionSummary}>
                <span>
                  고객 수정요청 <b>{proof.revision.used}회</b> / 무료 {proof.revision.freeLimit}회
                </span>
                <span>
                  남은 무료 수정 <b>{Math.max(0, proof.revision.freeLimit - proof.revision.used)}회</b>
                </span>
                {proof.revision.totalExtraFee > 0 && (
                  <span>
                    추가 수정 비용 <b>₩ {proof.revision.totalExtraFee.toLocaleString()}</b> (결제금액에 합산됨)
                  </span>
                )}
              </div>
            )}

            <div style={{ marginTop: 16 }}>
              <div className="card-title" style={{ marginBottom: 8 }}>
                1:1 상담 히스토리
              </div>
              {timeline.length > 0 ? (
                <ul className={styles.chat}>
                  {timeline.map((feedback, index) => {
                    const isAdminMsg = feedback.sender === 'ADMIN';
                    const round = rounds[index];
                    const items = revisionItemLabels(feedback.revision_items);
                    return (
                      <li key={index} className={`${styles.chatRow} ${isAdminMsg ? styles.chatAdmin : styles.chatCustomer}`}>
                        <div className={styles.chatMeta}>
                          <strong>{isAdminMsg ? '담당자' : '고객'}</strong>
                          {round > 0 ? (
                            <span className={`${styles.chatTag} ${styles.chatTagRevision}`}>
                              수정요청 {round}회차
                              {feedback.extra_fee ? ` · 유료 +₩ ${feedback.extra_fee.toLocaleString()}` : ''}
                            </span>
                          ) : (
                            <span className={styles.chatTag}>{isAdminMsg ? '답변' : '쪽지'}</span>
                          )}
                          <time>{formatDateTime(feedback.created_at)}</time>
                        </div>
                        <div className={`${styles.chatBubble} ${round > 0 ? styles.chatBubbleRevision : ''}`}>
                          {items.length > 0 && (
                            <div className={styles.chatItems}>
                              {items.map((label) => (
                                <span key={label}>{label}</span>
                              ))}
                            </div>
                          )}
                          <p>{feedback.message}</p>
                          {feedback.attachment_path && (
                            <a
                              href={apiUrl(`/uploads/${feedback.attachment_path}`)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={styles.chatAttachment}
                            >
                              <img src={apiUrl(`/uploads/${feedback.attachment_path}`)} alt="첨부 이미지" loading="lazy" />
                            </a>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p style={{ color: 'var(--ink-500)' }}>아직 주고받은 메시지가 없습니다.</p>
              )}

              <form className={styles.uploadForm} onSubmit={handleReplySubmit} style={{ marginTop: 12 }}>
                <label htmlFor="replyMessage">답변 남기기</label>
                <textarea
                  id="replyMessage"
                  rows={3}
                  value={replyMessage}
                  onChange={(event) => setReplyMessage(event.target.value)}
                  placeholder="고객에게 남길 답변을 입력해주세요"
                />
                <button type="submit" disabled={!replyMessage.trim() || isSendingReply}>
                  {isSendingReply ? '등록 중...' : '답변 등록'}
                </button>
                {replyError && <p className={styles.error}>{replyError}</p>}
              </form>
            </div>
          </div>
        )}

        <div className="card">
          <div className="card-title" style={{ marginBottom: 12 }}>
            새 시안 업로드
          </div>
          <form className={styles.uploadForm} onSubmit={handleUpload}>
            <label htmlFor="proofFile">파일 선택 (.jpg, .png, .pdf)</label>
            <input
              id="proofFile"
              type="file"
              accept=".jpg,.jpeg,.png,.pdf"
              onChange={(event) => setFile(event.target.files?.[0] ?? null)}
            />
            <button type="submit" disabled={!file || isUploading}>
              {isUploading ? '업로드 중...' : '시안 등록'}
            </button>
            {uploadError && <p className={styles.error}>{uploadError}</p>}
          </form>
        </div>
      </div>
    </AdminLayout>
  );
}

export default AdminOrderDetail;
